'use strict'

const MIN_CONTRAST = 4.6 // A small margin above WCAG AA's 4.5:1 for normal text.
const DARK_TEXT = [16, 33, 45]

function luminance(rgb) {
  const channels = rgb.map(value => {
    const channel = value / 255
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722
}

function chooseHeroContrast(samples) {
  const candidates = [
    {text: [255, 255, 255], overlay: 0},
    {text: DARK_TEXT, overlay: 255},
  ].map(candidate => {
    const foreground = luminance(candidate.text)
    const contrastAt = opacity => samples.reduce((minimum, rgb) => {
      const background = luminance(rgb.map(value => value * (1 - opacity) + candidate.overlay * opacity))
      return Math.min(minimum, (Math.max(foreground, background) + 0.05) / (Math.min(foreground, background) + 0.05))
    }, Infinity)
    const contrast = contrastAt(0)
    let opacity = 0
    if (contrast < MIN_CONTRAST) {
      let low = 0
      let high = 0.65
      for (let step = 0; step < 8; step++) {
        const middle = (low + high) / 2
        if (contrastAt(middle) >= MIN_CONTRAST) high = middle
        else low = middle
      }
      opacity = Math.ceil(high * 100) / 100
    }
    return {...candidate, opacity, contrast}
  })

  // Use the least intrusive scrim; on uniform backgrounds prefer the stronger contrast.
  return candidates.sort((a, b) => a.opacity - b.opacity || b.contrast - a.contrast)[0]
}

function initShowcaseHeroContrast(hero) {
  if (!hero || !hero.dataset.showcaseHeroImage) return

  const image = new Image()
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return // CSS keeps white text on a dark scrim without canvas support.

  let observer
  let frame
  let failed = false
  const useFallback = error => {
    failed = true
    if (observer) observer.disconnect()
    hero.style.removeProperty('--showcase-hero-text')
    hero.style.removeProperty('--showcase-hero-overlay-rgb')
    hero.style.removeProperty('--showcase-hero-overlay-opacity')
    console.warn('Showcase cover contrast unavailable; using the readable fallback.', error)
  }

  const update = () => {
    if (failed || !image.naturalWidth) return
    const bounds = hero.getBoundingClientRect()
    if (!bounds.width || !bounds.height) return

    // Match background-size: cover and background-position: center at this viewport.
    canvas.width = Math.ceil(bounds.width)
    canvas.height = Math.ceil(bounds.height)
    const cover = Math.max(bounds.width / image.naturalWidth, bounds.height / image.naturalHeight)
    context.fillStyle = '#10212d' // Also the CSS base beneath transparent PNGs.
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image,
      (bounds.width - image.naturalWidth * cover) / 2,
      (bounds.height - image.naturalHeight * cover) / 2,
      image.naturalWidth * cover, image.naturalHeight * cover)

    try {
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
      const samples = []
      hero.querySelectorAll('.showcase-hero__eyebrow, .showcase-hero__title, .showcase-hero__lead').forEach(element => {
        const rect = element.getBoundingClientRect()
        const left = Math.max(0, Math.floor(rect.left - bounds.left))
        const right = Math.min(canvas.width, Math.ceil(rect.right - bounds.left))
        const top = Math.max(0, Math.floor(rect.top - bounds.top))
        const bottom = Math.min(canvas.height, Math.ceil(rect.bottom - bounds.top))
        if (right <= left || bottom <= top) return
        const darkest = [255, 255, 255]
        const lightest = [0, 0, 0]
        for (let y = top; y < bottom; y++) {
          for (let x = left; x < right; x++) {
            const offset = (y * canvas.width + x) * 4
            for (let channel = 0; channel < 3; channel++) {
              darkest[channel] = Math.min(darkest[channel], pixels[offset + channel])
              lightest[channel] = Math.max(lightest[channel], pixels[offset + channel])
            }
          }
        }
        // Channel bounds conservatively cover every pixel, including fine patterns
        // that would disappear if the image were averaged into a small thumbnail.
        samples.push(darkest, lightest)
      })
      if (!samples.length) return

      const {text, overlay, opacity} = chooseHeroContrast(samples)
      hero.style.setProperty('--showcase-hero-text', `rgb(${text.join(', ')})`)
      hero.style.setProperty('--showcase-hero-overlay-rgb', `${overlay}, ${overlay}, ${overlay}`)
      hero.style.setProperty('--showcase-hero-overlay-opacity', opacity)
    } catch (error) {
      useFallback(error) // Remote images without CORS must not leave unreadable text.
    }
  }

  const schedule = () => {
    if (failed) return
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(update)
  }
  image.onload = () => {
    update()
    if (failed) return
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(schedule)
      observer.observe(hero)
    }
    window.addEventListener('resize', schedule, {passive: true})
    if (document.fonts) document.fonts.ready.then(schedule)
  }
  image.onerror = useFallback
  if (new URL(hero.dataset.showcaseHeroImage, window.location.href).origin !== window.location.origin) {
    image.crossOrigin = 'anonymous'
  }
  image.src = hero.dataset.showcaseHeroImage
}

module.exports = {chooseHeroContrast, initShowcaseHeroContrast}
